import { FormInstance } from "antd";
import { OpForm } from "components/customAntd/DLS/OpForm/OpForm";
import { OpTable } from "components/customAntd/DLS/OpTable/OpTable";
import { OpTableRawColumnType } from "components/customAntd/DLS/OpTableCore/OpTableCore";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "store/store";
import { ConfigItem, InvitationConfig } from "types/visitorInvitationTypes";

interface FieldsFormProps {
    form: FormInstance;
    invitationConfig: InvitationConfig;
}

const FieldsForm: React.FC<FieldsFormProps> = ({ form, invitationConfig }) => {
    const customFields = useSelector((state: RootState) => state.customFields.customFields);

    const { t } = useTranslation();

    // Extract only the objects (ConfigItem) and turn them into an array of objects
    const configItemsArray = Object.keys(invitationConfig)
        .filter(
            key => typeof invitationConfig[key as keyof InvitationConfig] === 'object' &&
                invitationConfig[key as keyof InvitationConfig] !== null
        )
        .map(key => ({
            field: key,
            ...invitationConfig[key as keyof InvitationConfig] as ConfigItem,
        }));

    const extendedFields = [
        ...configItemsArray,
        ...customFields.map(customField => ({
            field: `${customField.id}`,
            name: customField.name,
            configurable: true,
            included: false,
            required: false,
        })),
    ];

    const columns: OpTableRawColumnType[] = [
        {
            dataIndex: 'field',
            label: t('invitations.fieldsTable.field'),
            render: (text, record) => {
                const translationKey = `invitations.fieldsTable.${record.field}`;
                const translatedName = t(translationKey);

                let fieldName = translatedName !== translationKey
                    ? translatedName
                    : (record.name || text.replace(/([A-Z])/g, ' $1').replace(/^./, (str: string) => str.toUpperCase()));

                // Apply specific label changes
                if (fieldName.trim() === "Visit Company") {
                    fieldName = "Visitor Company";
                } else if (fieldName.trim() === "Visit Phone") {
                    fieldName = "Visitor Phone";
                }

                return <span>{fieldName}</span>;
            }
        },
        {
            dataIndex: 'included',
            label: t('invitations.fieldsTable.enabled'),
            render: (included: boolean, record) => {
                const handleEnabledChange = (checked: boolean) => {
                    form.setFieldsValue({
                        [record.field]: {
                            ...record,
                            included: checked,
                            required: checked ? form.getFieldValue([record.field, 'required']) : false
                        }
                    });
                };
                return (
                    <OpForm.Switch
                        name={[record.field, 'included']}
                        onChange={handleEnabledChange}
                        disabled={!record.configurable}
                    />
                );
            }
        },
        {
            dataIndex: 'required',
            label: t('invitations.fieldsTable.required'),
            render: (included: boolean, record) => {
                const handleRequiredChange = (checked: boolean) => {
                    form.setFieldsValue({
                        [record.field]: {
                            ...record,
                            included: checked ? true : form.getFieldValue([record.field, 'included']),
                            required: checked
                        }
                    });
                };
                return (
                    <OpForm.Switch
                        name={[record.field, 'required']}
                        onChange={handleRequiredChange}
                        disabled={!record.configurable}
                    />
                );
            }
        }
    ];

    return (
        <OpTable
            label={t('invitations.fieldsTable.label')}
            dataSource={extendedFields}
            pagination={false}
            allowGlobalSearch={false}
            allowExport={false}
            allowShowHideColumns={false}
            columns={columns}
            style={{ marginBottom: '16px' }}
            rowKey="field"
        />
    );
};

export default FieldsForm;
